/**
 * Employer request detail — one contact request, expanded fully.
 *
 * Reached via /employer/requests/:id from EmployerRequests.jsx list. Shows:
 *   · Candidate identity + link back to their résumé (/r/<public_id>)
 *   · Employer's original message
 *   · Timeline: sent → responded (+ any reply)
 *   · Status pill + role_title
 *   · Copy-to-clipboard on the candidate reply so recruiters can paste it
 *     into their ATS / CRM without retyping.
 *
 * Gated to verified employers via <EmployerProtected> in App.js.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, User, Briefcase, Clock, CheckCircle2, XCircle,
  ExternalLink, Copy, Loader, Mail, LogOut,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployerRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [replyCopied, setReplyCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/employer/contact-requests`,
        { withCredentials: true });
      const rows = r.data?.requests || [];
      const found = rows.find((row) => row.id === id);
      if (found) setReq(found); else setNotFound(true);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copyReply = async () => {
    if (!req?.candidate_reply) return;
    try {
      await navigator.clipboard.writeText(req.candidate_reply);
      setReplyCopied(true);
      setTimeout(() => setReplyCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/employer/logout`, {},
        { withCredentials: true });
    } catch { /* ignore */ }
    navigate("/employer/login", { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      <div style={{ background: "#0F1B2D" }}>
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#FFC145", fontWeight: 700 }}>
              · Resmo Talent Directory · Request
            </div>
            <div className="font-display uppercase leading-tight"
                 style={{ fontSize: "1.2rem", color: "white", letterSpacing: "-0.01em" }}>
              {req?.candidate_display || "Loading…"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/employer/requests"
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "transparent", color: "#FFC145",
                           border: "1px solid #FFC145", fontWeight: 700, textDecoration: "none" }}
                  data-testid="employer-request-detail-back">
              <ArrowLeft size={11} /> Outbox
            </Link>
            <button onClick={logout}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "transparent", color: "#FFC145",
                             border: "1px solid #FFC145", fontWeight: 700 }}>
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
        {loading && (
          <div className="text-center py-14">
            <Loader className="mx-auto animate-spin" size={22} style={{ color: "#FF6A00" }} />
          </div>
        )}
        {!loading && notFound && (
          <div className="text-center py-14 rounded-md"
               style={{ background: "white", border: "1.5px dashed #CBD5E1" }}>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#94A3B8" }}>· Not found</div>
            <div className="text-[13px] mt-1" style={{ color: "#475569" }}>
              That request doesn&apos;t exist or belongs to a different account.
            </div>
            <Link to="/employer/requests"
                  className="inline-block mt-3 font-mono-rm text-[11px] uppercase tracking-widest"
                  style={{ color: "#FF6A00", fontWeight: 700 }}>
              ← Back to outbox
            </Link>
          </div>
        )}
        {req && (
          <div className="space-y-4">
            <MetaCard req={req} />
            <MessageCard title="Your original message" text={req.message}
                         accent="#94A3B8" bg="#F8FAFC" testid="employer-detail-message" />
            {req.candidate_reply && (
              <MessageCard title={`${req.candidate_display}'s reply`}
                           text={req.candidate_reply}
                           accent="#0E5A4A" bg="#D1FAE5"
                           testid="employer-detail-reply"
                           extra={
                             <button onClick={copyReply}
                                     className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                                     style={{ background: "white", color: "#0E5A4A",
                                              border: "1.5px solid #0E5A4A", fontWeight: 700 }}
                                     data-testid="employer-detail-copy-reply">
                               <Copy size={11} /> {replyCopied ? "Copied ✓" : "Copy reply"}
                             </button>
                           } />
            )}
            {req.status === "accepted" && !req.candidate_reply && (
              <div className="rounded p-3 text-[13px]"
                   style={{ background: "#D1FAE5", color: "#064E3B",
                            border: "1.5px dashed #0E5A4A" }}>
                <CheckCircle2 size={13} className="inline mr-1" />
                Accepted — no written reply. The candidate should reach out via email directly.
              </div>
            )}
            {req.status === "declined" && (
              <div className="rounded p-3 text-[13px]"
                   style={{ background: "#FEE2E2", color: "#7C2D12",
                            border: "1.5px dashed #B91C1C" }}>
                <XCircle size={13} className="inline mr-1" />
                Not a fit right now. Please respect the candidate&apos;s decision — we enforce a 14-day cool-down before you can reach out again with a different pitch.
              </div>
            )}
            {req.status === "pending" && (
              <div className="rounded p-3 text-[13px]"
                   style={{ background: "#FFF4E0", color: "#B45309",
                            border: "1.5px dashed #FFC145" }}>
                <Clock size={13} className="inline mr-1" />
                Awaiting the candidate&apos;s response. We&apos;ll email you the moment they accept or decline.
              </div>
            )}
            <TimelineCard req={req} />
          </div>
        )}
      </div>
    </div>
  );
}

function MetaCard({ req }) {
  return (
    <div className="rounded-md p-4"
         style={{ background: "white", border: "1.5px solid #0F1B2D",
                  boxShadow: "2px 2px 0 0 #FFC145" }}
         data-testid="employer-detail-meta">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={req.status} />
            <span className="font-display uppercase leading-tight"
                  style={{ fontSize: "1.35rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              {req.candidate_display}
            </span>
          </div>
          <div className="text-[13px] mt-2" style={{ color: "#334155" }}>
            <Briefcase size={13} className="inline mr-1" style={{ color: "#FF6A00" }} />
            <b>{req.role_title}</b>
          </div>
          <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>
            <Clock size={11} className="inline mr-1" />
            Sent {new Date(req.created_at).toLocaleString()}
            {req.responded_at && (
              <> · Answered {new Date(req.responded_at).toLocaleString()}</>
            )}
          </div>
        </div>
        {req.publication_id && (
          <Link to={`/r/${req.publication_id}`}
                className="inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-mono-rm uppercase tracking-widest"
                style={{ background: "#0F1B2D", color: "#FFC145",
                         border: "1.5px solid #0F1B2D", fontWeight: 700, textDecoration: "none",
                         boxShadow: "2px 2px 0 0 #FF6A00" }}
                data-testid="employer-detail-view-resume">
            <ExternalLink size={11} /> View résumé
          </Link>
        )}
      </div>
    </div>
  );
}

function MessageCard({ title, text, accent, bg, testid, extra }) {
  return (
    <div className="rounded-md p-4"
         style={{ background: "white", border: `1.5px solid ${accent === "#94A3B8" ? "#CBD5E1" : accent}` }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-mono-rm text-[10px] uppercase tracking-widest"
             style={{ color: accent, fontWeight: 700, letterSpacing: "0.12em" }}>
          · {title}
        </div>
        {extra}
      </div>
      <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap rounded"
           style={{ background: bg, border: "1px dashed rgba(15,27,45,0.15)",
                    padding: "12px 14px", color: "#334155" }}
           data-testid={testid}>
        {text}
      </div>
    </div>
  );
}

function TimelineCard({ req }) {
  const events = [
    { ts: req.created_at, label: `Sent request to ${req.candidate_display}`,
      icon: Mail, color: "#0F1B2D" },
    ...(req.responded_at ? [{
      ts: req.responded_at,
      label: req.status === "accepted"
        ? `Accepted by ${req.candidate_display}`
        : `Declined by ${req.candidate_display}`,
      icon: req.status === "accepted" ? CheckCircle2 : XCircle,
      color: req.status === "accepted" ? "#0E5A4A" : "#B91C1C",
    }] : []),
  ];
  return (
    <div className="rounded-md p-4"
         style={{ background: "white", border: "1.5px dashed #CBD5E1" }}
         data-testid="employer-detail-timeline">
      <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-3"
           style={{ color: "#94A3B8", fontWeight: 700, letterSpacing: "0.12em" }}>
        · Timeline
      </div>
      <ol className="space-y-2.5">
        {events.map((e, i) => {
          const Icon = e.icon;
          return (
            <li key={i} className="flex items-start gap-2.5">
              <Icon size={14} style={{ color: e.color, flexShrink: 0, marginTop: 2 }} />
              <div className="min-w-0">
                <div className="text-[13px]" style={{ color: "#334155" }}>{e.label}</div>
                <div className="text-[11px]" style={{ color: "#94A3B8" }}>
                  {new Date(e.ts).toLocaleString()}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = ({
    pending:  { bg: "#FFF4E0", color: "#FF6A00", label: "Pending" },
    accepted: { bg: "#D1FAE5", color: "#0E5A4A", label: "Accepted" },
    declined: { bg: "#FEE2E2", color: "#B91C1C", label: "Declined" },
    expired:  { bg: "#F1F5F9", color: "#64748B", label: "Expired" },
  }[status] || { bg: "#F1F5F9", color: "#64748B", label: status });
  return (
    <span className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}>
      {meta.label}
    </span>
  );
}
